import {
  AfterViewInit,
  Directive,
  ElementRef,
  Renderer2,
  computed,
  effect,
  inject,
  input,
  signal
} from '@angular/core';

/**
 * This directive is used to detect if content is clamped. Clamped content overflow is hidden and gets an ellipsis.
 * There is now other way to detect content is clamped so we need toi compare the target height and the content height.
 * Optionally you can specify an icon that needs to be cloned and display at the end of the ellipses.
 */
@Directive({
  selector: '[lineClampDetector]'
})
export class LineClampDetectorDirective implements AfterViewInit {
  /**
   * The target element to detect if it is clamped, this element has the css line-clamp limiting it to a number of rows
   */
  public readonly lineClampTarget = input.required<string>();
  /**
   * The content element to detect if it is clamped, when this content exceeds the number of lines the clamp target will be marked as 'is-clamped'
   */
  public readonly lineClampContent = input.required<string>();
  /**
   * Optionally you can specify an icon that needs to be cloned and display at the end of the ellipses.
   */
  public readonly lineClampIcon = input<string | undefined>();

  private readonly viewReady = signal(false);

  protected readonly targetElement = computed(() => {
    if (!this.viewReady()) {
      return null;
    }
    return this.host.nativeElement.querySelector(this.lineClampTarget()) as HTMLElement | null;
  });

  protected readonly contentElement = computed(() => {
    if (!this.viewReady()) {
      return null;
    }
    return this.host.nativeElement.querySelector(this.lineClampContent()) as HTMLElement | null;
  });

  private readonly iconElement = signal<HTMLElement | null>(null);

  private resizeObserver?: ResizeObserver;
  private clonedIconElement?: HTMLElement;
  private updateClampedStateScheduled = false;

  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);

  constructor() {
    effect((onCleanup) => {
      if (!this.viewReady()) {
        return;
      }
      const target = this.targetElement();
      const content = this.contentElement();
      this.iconElement(); // track so we re-run when icon input changes
      if (!target || !content) {
        return;
      }

      this.resizeObserver = new ResizeObserver(() => this.triggerUpdateClampedState());
      this.resizeObserver.observe(target);
      this.resizeObserver.observe(content);

      onCleanup(() => this.cleanup());

      this.triggerUpdateClampedState();
    });

    effect(() => {
      const lineClampIcon = this.lineClampIcon();
      this.triggerResolveIcon(lineClampIcon);
    });
  }

  public ngAfterViewInit(): void {
    this.viewReady.set(true);
  }

  private cleanup(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;

    if (this.clonedIconElement) {
      const content = this.contentElement();
      if (content && this.clonedIconElement.parentNode === content) {
        this.renderer.removeChild(content, this.clonedIconElement);
      }
      this.clonedIconElement = undefined;
    }
  }

  private triggerResolveIcon(lineClampIcon: string | undefined): void {
    requestAnimationFrame(() => {
      const icon = lineClampIcon ? (this.host.nativeElement.querySelector(lineClampIcon) as HTMLElement | null) : null;
      this.iconElement.set(icon);
    });
  }

  private triggerUpdateClampedState(): void {
    if (this.updateClampedStateScheduled) {
      return;
    }
    this.updateClampedStateScheduled = true;
    requestAnimationFrame(() => {
      this.updateClampedStateScheduled = false;
      this.updateClampedState();
    });
  }

  private updateClampedState(): void {
    const target = this.targetElement();
    const content = this.contentElement();
    const icon = this.iconElement();
    if (!target || !content) {
      return;
    }
    const visibleHeight = target.clientHeight;
    const realHeight = content.offsetHeight;
    const isClamped = realHeight > visibleHeight + 1;
    if (isClamped) {
      this.renderer.addClass(this.host.nativeElement, 'is-clamped');

      if (icon && !this.clonedIconElement) {
        this.clonedIconElement = icon.cloneNode(true) as HTMLElement;
        this.renderer.addClass(this.clonedIconElement, 'line-clamp-detector-icon');
        this.renderer.setStyle(this.clonedIconElement, 'left', `${content.offsetWidth}px`);
        this.renderer.setStyle(this.clonedIconElement, 'top', `${target.offsetHeight}px`);
        this.renderer.appendChild(content, this.clonedIconElement);
      }
    } else {
      this.renderer.removeClass(this.host.nativeElement, 'is-clamped');

      if (this.clonedIconElement) {
        this.renderer.removeChild(content, this.clonedIconElement);
        this.clonedIconElement = undefined;
      }
    }
  }
}
